import type { history } from 'instantsearch.js/es/lib/routers'
import type { BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history'
import type {
  IndexUiState,
  Router,
  StateMapping,
  UiState,
} from 'instantsearch.js/es/types'
import { omit } from 'remeda'

// `configure` matches instantsearch's own `simple` mapping. `page` is also stripped: `SearchResults`'
// `showPrevious` was meant to replay it and restore the reading position on a deep link, but that
// never actually worked on a cold load — the only case where it appeared to work (browser back
// button) is the back-forward cache restoring the whole page verbatim, which doesn't read the URL
// at all. Keeping `page` only added filter+page combinations to the URL for no real benefit.
const reservedIndexKeys = ['configure', 'page'] as const

type PlpIndexRouteState = Record<string, unknown>

// Loose on purpose: the query string can hold keys that aren't index state (the SEO `?page`).
type PlpRouteState = Record<string, unknown>

const stripReservedKeys = (
  indexState: Record<string, unknown>,
): PlpIndexRouteState => {
  return omit(indexState, reservedIndexKeys)
}

/**
 * Index entries are objects; a leftover top-level key like the SEO `?page` parses as a string.
 */
const isIndexRouteEntry = (
  entry: [string, unknown],
): entry is [string, Record<string, unknown>] => {
  return typeof entry[1] === 'object' && entry[1] !== null
}

/**
 * VAMS-580 — the `{ router, stateMapping }` pair InstantSearch uses for PLP/search routing.
 *
 * Routing is always-on because the `routing` prop is not reactive after mount, so enabling it once
 * a filter is applied is a no-op — that was the bug. The default router would then wipe the SEO
 * `?page` (VAMS-544), hence this custom one: it keeps Algolia's params and the SEO `?page` apart.
 *
 * `historyRouter` is injected so this file needs no runtime instantsearch dependency.
 */
export const buildPlpAlgoliaRouting = ({
  historyRouter,
  getLocation,
}: {
  historyRouter: typeof history
  /** Tests pass a stub so the real router runs in node; production uses `window.location`. */
  getLocation?: BrowserHistoryArgs<PlpRouteState>['getLocation']
}): {
  router: Router<PlpRouteState>
  stateMapping: StateMapping<UiState, PlpRouteState>
} => {
  const router = historyRouter<PlpRouteState>({
    getLocation,
    createURL({ qsModule, routeState, location }) {
      const { protocol, hostname, port, pathname, hash, search } = location
      const portSuffix = port ? `:${port}` : ''
      const origin = `${protocol}//${hostname}${portSuffix}`
      const filterQuery = qsModule.stringify(routeState)

      // filtered → shareable URL; the SEO `?page` addresses the unfiltered grid, so it goes
      if (filterQuery) {
        return `${origin}${pathname}?${filterQuery}${hash}`
      }

      // unfiltered → keep the SEO `?page`, re-encoded so reserved characters can't inject a param
      const page = new URLSearchParams(search).get('page')

      return page
        ? `${origin}${pathname}?${qsModule.stringify({ page })}${hash}`
        : `${origin}${pathname}${hash}`
    },
    parseURL({ qsModule, location }) {
      const parsed = qsModule.parse(location.search.slice(1), {
        arrayLimit: 99,
      })

      // the SEO `?page` must never seed Algolia's own state
      return omit(parsed, ['page'] as const)
    },
  })

  const stateMapping: StateMapping<UiState, PlpRouteState> = {
    stateToRoute: (uiState) => {
      return Object.fromEntries(
        Object.entries(uiState).map(
          ([indexId, indexUiState]): [string, PlpIndexRouteState] => {
            return [indexId, stripReservedKeys(indexUiState)]
          },
        ),
      )
    },
    routeToState: (routeState) => {
      return Object.fromEntries(
        Object.entries(routeState)
          .filter(isIndexRouteEntry)
          .map(([indexId, indexRouteState]): [string, IndexUiState] => {
            // same trust instantsearch's own `simple` mapping takes on a parsed URL
            return [indexId, stripReservedKeys(indexRouteState)]
          }),
      )
    },
  }

  return { router, stateMapping }
}
